import React, {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  Banner,
  Button,
  Checkbox,
  FieldBox,
  Link,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color, Tag } from '@pushwoosh/kit-constants';
import { Horizontal } from '@pushwoosh/kit-helpers';
import { Text } from '@pushwoosh/kit-typography';

import { H1 } from '~/src/ui-kit';
import { useEnvironment } from '~/src/contexts/environment';
import { RECAPTCHA_ACTIONS, useRecaptcha } from '~/src/components/Recaptcha';
import { ButtonLoader } from '~/src/components/ButtonLoader';
import { ContentBox } from '~/src/components/ContentBox';
import { GoogleLogo } from '~/src/components/GoogleLogo';
import { Separator } from '~/src/components/Separator';
import { browserSupportsPasskeys } from '~/src/helpers/passkey';
import { Lang, useLang } from '~/src/polyglot';

import type { LoginPageProps } from './LoginPage.types';
import {
  FormStyled,
  InputFieldsBox,
  SideOptionsBox,
  LinkStyled,
  ButtonsBox,
} from './styles';

export function LoginPage({
  inviteCode,
  inviteEmail,
  isLoadedInviteEmail,
  loginHandler,
  checkForSSOHandler,
  passkeyLoginHandler,
  isSSOChecked,
  errorMessage,
}: LoginPageProps): ReactElement {
  const { getLinkByName, productName } = useEnvironment();
  const { getRecaptchaToken } = useRecaptcha(productName);
  const lang = useLang();
  const [isShowPassword, setShowPassword] = useState(false);
  const [isPasskeyInFlight, setPasskeyInFlight] = useState(false);
  const navigate = useNavigate();

  const isAllowRegister = productName === 'Pushwoosh';
  const isAllowGoogleLogin = productName === 'Pushwoosh' || productName === 'PushwooshUS';
  const isAllowPasskeyLogin = browserSupportsPasskeys();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    getValues,
    setValue,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      password: '',
      username: '',
    },
  });

  useEffect(() => {
    setValue('username', inviteEmail);
  }, [inviteEmail, setValue]);

  async function login(): Promise<void> {
    !isSSOChecked
      ? await checkForSSOHandler({
        token: await getRecaptchaToken(RECAPTCHA_ACTIONS.ssoCheck),
        username: getValues('username'),
      })
      : await loginHandler({
        token: await getRecaptchaToken(RECAPTCHA_ACTIONS.login),
        password: getValues('password'),
        username: getValues('username'),
      });
  }

  const onPasskeyClick = useCallback(async (): Promise<void> => {
    setPasskeyInFlight(true);
    try {
      await passkeyLoginHandler();
    } finally {
      setPasskeyInFlight(false);
    }
  }, [passkeyLoginHandler]);

  return (
    <>
      <ContentBox>
        <FormStyled onSubmit={handleSubmit(login)}>
          <H1><Lang message="LOGIN.TITLE" productName={productName} /></H1>
          <InputFieldsBox>
            <FieldBox
              title={lang({ message: 'LOGIN.FIELD.USERNAME' })}
              error={!!errors.username && (
                <Text $variant="footnote" $color={Color.DANGER}>
                  <Lang message="LOGIN.ERROR.INVALID_EMAIL" />
                </Text>
              )}
            >
              <NativeInput
                autoFocus={!isSSOChecked}
                // "username webauthn" is the WebAuthn conditional-UI hook:
                // when the browser sees this on an input, it will surface
                // available passkeys for this origin in the field's autofill
                // dropdown. The conditional get() is armed from LoginPage.tsx
                // on mount; this attribute is the DOM-side half.
                autoComplete="username webauthn"
                disabled={!isLoadedInviteEmail || inviteEmail !== ''}
                $isErrored={!!errors.username}
                {...register('username', { required: true })}
              />
            </FieldBox>
            {isSSOChecked && (
              <>
                <FieldBox
                  title={lang({ message: 'LOGIN.FIELD.PASSWORD' })}
                  error={!!errors.password && (
                    <Text $variant="footnote" $color={Color.DANGER}>
                      <Lang message="LOGIN.ERROR.INVALID_PASSWORD" />
                    </Text>
                  )}
                >
                  <NativeInput
                    type={isShowPassword ? 'text' : 'password'}
                    autoFocus={isSSOChecked}
                    $isErrored={!!errors.password}
                    {...register('password', { required: true })}
                  />
                </FieldBox>
                <SideOptionsBox>
                  <Checkbox
                    name="show-password"
                    label={lang({ message: 'LOGIN.CHECKBOX.SHOW_PASSWORD' })}
                    isChecked={isShowPassword}
                    onChange={(isChecked): void => setShowPassword(isChecked)}
                  />
                  <LinkStyled
                    as={LinkStyled}
                    title={lang({ message: 'LOGIN.LINK.FORGOT_PASSWORD' })}
                    to={`${getLinkByName('resetPassword')}?email=${encodeURIComponent(watch('username'))}`}
                  >
                    <Lang message="LOGIN.LINK.FORGOT_PASSWORD" />
                  </LinkStyled>
                </SideOptionsBox>
              </>
            )}
            {!!errorMessage && !isSubmitting && <Banner type="error" title={errorMessage} />}
          </InputFieldsBox>
          <ButtonsBox>
            <Button
              type="submit"
              color="primary"
              isLoading={isSubmitting}
            >
              <Lang message="LOGIN.BUTTON.SIGN_IN" />
            </Button>
            {isAllowGoogleLogin && (
              <>
                <Separator />
                {isSubmitting ? (
                  <ButtonLoader>
                    <Horizontal $alignItems="center" $gap={6}>
                      <GoogleLogo opacity="50%" />
                      <Lang message="LOGIN.BUTTON.GOOGLE" />
                    </Horizontal>
                  </ButtonLoader>
                ) : (
                  <Button
                    as="a"
                    type="button"
                    color="secondary"
                    href={inviteCode === '' ? '/api/auth-with-google' : `/api/auth-with-google?invite=${inviteCode}`}
                  >
                    <Horizontal $alignItems="center" $gap={6}>
                      <GoogleLogo />
                      <Lang message="LOGIN.BUTTON.GOOGLE" />
                    </Horizontal>
                  </Button>
                )}
              </>
            )}
            {isAllowPasskeyLogin && (
              <>
                <Separator />
                <Button
                  type="button"
                  color="secondary"
                  isLoading={isPasskeyInFlight}
                  onClick={onPasskeyClick}
                >
                  <Lang message="LOGIN.BUTTON.PASSKEY" />
                </Button>
              </>
            )}
          </ButtonsBox>
        </FormStyled>
      </ContentBox>
      {isAllowRegister && (
        <Horizontal $alignItems="center" $gap={4}>
          <Lang message="LOGIN.FOOTER.NO_ACCOUNT" />
          <Link
            as={Tag.BUTTON}
            title={lang({ message: 'LOGIN.LINK.SIGN_UP' })}
            onClick={(): void => {
              navigate(getLinkByName('register'));
            }}
          >
            <Lang message="LOGIN.LINK.SIGN_UP" />
          </Link>
        </Horizontal>
      )}
    </>
  );
}
